import React from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  VideoCall as SkypeIcon
} from '@mui/icons-material';
// import { useCatalogService } from '../../services/catalogService';

// Tipos para contactos
interface SpecialistContact {
  contactId?: number;
  contactType: string;
  contactValue: string;
  isPrimary: boolean;
  notes?: string;
  isActive?: boolean;
}

interface SpecialistContactFormProps {
  contacts: SpecialistContact[];
  onChange: (contacts: SpecialistContact[]) => void;
  disabled?: boolean;
  contactTypes?: any[]; // ✅ NUEVO: Recibir tipos de contacto como prop
}

const SpecialistContactForm: React.FC<SpecialistContactFormProps> = ({
  contacts,
  onChange,
  disabled = false,
  contactTypes = [] // ✅ NUEVO: Usar tipos de contacto pasados como prop
}) => {
  // ✅ Ya no necesitamos estado local ni useEffect - recibimos contactTypes como prop

  const addContact = () => {
    const newContact: SpecialistContact = {
      contactType: '',
      contactValue: '',
      isPrimary: false,
      notes: '',
      isActive: true
    };
    onChange([...contacts, newContact]);
  };

  const updateContact = (index: number, field: keyof SpecialistContact, value: any) => {
    const updatedContacts = [...contacts];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    onChange(updatedContacts);
  };

  const removeContact = (index: number) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    onChange(updatedContacts);
  };

  const getContactIcon = (contactType: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      'EMAIL': <EmailIcon fontSize="small" />,
      'PHONE_OFFICE': <PhoneIcon fontSize="small" />,
      'PHONE_MOBILE': <PhoneIcon fontSize="small" />,
      'WHATSAPP': <WhatsAppIcon fontSize="small" />,
      'TELEGRAM': <TelegramIcon fontSize="small" />,
      'SKYPE': <SkypeIcon fontSize="small" />
    };
    return icons[contactType] || <PhoneIcon fontSize="small" />;
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Medios de Contacto
      </Typography>
      
      {contacts.map((contact, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2, position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {contact.contactType && getContactIcon(contact.contactType)}
            <Typography variant="subtitle2" sx={{ ml: 1 }}>
              Medio de Contacto {index + 1}
            </Typography>
            {!disabled && (
              <IconButton
                size="small"
                onClick={() => removeContact(index)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de Contacto</InputLabel>
                <Select
                  value={contact.contactType}
                  onChange={(e) => updateContact(index, 'contactType', e.target.value)}
                  label="Tipo de Contacto"
                  disabled={disabled}
                >
                  <MenuItem value="">
                    <em>Seleccionar tipo...</em>
                  </MenuItem>
                  {contactTypes.map((type: any) => (
                    <MenuItem key={type.code} value={type.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getContactIcon(type.code)}
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="body2">{type.description}</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor"
                value={contact.contactValue}
                onChange={(e) => updateContact(index, 'contactValue', e.target.value)}
                placeholder="dr@example.com, 555-1234"
                required
                disabled={disabled}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Notas"
                value={contact.notes || ''}
                onChange={(e) => updateContact(index, 'notes', e.target.value)}
                placeholder="Teléfono de emergencia, horario de atención"
                disabled={disabled}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contact.isPrimary}
                    onChange={(e) => updateContact(index, 'isPrimary', e.target.checked)}
                    disabled={disabled}
                  />
                }
                label="Contacto Principal"
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </Paper>
      ))}

      {!disabled && (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addContact}
          fullWidth
          sx={{ mt: 1 }}
        >
          Agregar Medio de Contacto
        </Button>
      )}

      {contacts.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2" color="text.secondary">
            No hay medios de contacto agregados. Haz clic en "Agregar Medio de Contacto" para comenzar.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SpecialistContactForm;
